import { arrayMove } from '@dnd-kit/sortable';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { QUESTION_CATEGORIES } from '../interviews.types';
import type { QuestionCategory, WizardQuestion } from '../interviews.types';
import { QuestionCategorySection } from '../question-category-section';

import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';

type QuestionsStepProps = {
  questions: WizardQuestion[];
  isGenerating: boolean;
  onQuestionsChange: (questions: WizardQuestion[]) => void;
  onBack: () => void;
  onSave: () => void;
  isSaving: boolean;
};

export function QuestionsStep({
  questions,
  isGenerating,
  onQuestionsChange,
  onBack,
  onSave,
  isSaving
}: QuestionsStepProps): React.ReactNode {
  const { t } = useTranslation();

  function getQuestionsForCategory(category: QuestionCategory): WizardQuestion[] {
    return questions.filter((q) => q.category === category);
  }

  function handleUpdate(
    category: QuestionCategory,
    localIndex: number,
    updated: WizardQuestion
  ): void {
    const categoryQuestions = getQuestionsForCategory(category);
    const targetQuestion = categoryQuestions[localIndex];
    if (!targetQuestion) return;
    const globalIndex = questions.indexOf(targetQuestion);
    const next = [...questions];
    next[globalIndex] = updated;
    onQuestionsChange(next);
  }

  function handleDelete(category: QuestionCategory, localIndex: number): void {
    const categoryQuestions = getQuestionsForCategory(category);
    const targetQuestion = categoryQuestions[localIndex];
    if (!targetQuestion) return;
    const globalIndex = questions.indexOf(targetQuestion);
    onQuestionsChange(questions.filter((_, i) => i !== globalIndex));
  }

  function handleReorder(category: QuestionCategory, activeId: string, overId: string): void {
    const categoryQuestions = getQuestionsForCategory(category);
    const oldIndex = categoryQuestions.findIndex((q) => q.id === activeId);
    const newIndex = categoryQuestions.findIndex((q) => q.id === overId);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(categoryQuestions, oldIndex, newIndex);
    const otherQuestions = questions.filter((q) => q.category !== category);
    onQuestionsChange([...otherQuestions, ...reordered]);
  }

  function handleAdd(category: QuestionCategory): void {
    const categoryQuestions = getQuestionsForCategory(category);
    const maxPosition = categoryQuestions.reduce((max, q) => Math.max(max, q.position), -1);
    const newQuestion: WizardQuestion = {
      id: crypto.randomUUID(),
      category,
      text: '',
      position: maxPosition + 1,
      isOptional: false,
      aiGenerated: false
    };
    onQuestionsChange([...questions, newQuestion]);
  }

  if (isGenerating) {
    return (
      <div className="flex flex-1 min-h-0 flex-col gap-4 overflow-y-auto">
        <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-4">
          <Loader2 className="size-5 animate-spin text-primary" />
          <div>
            <p className="text-sm font-medium">{t('wizard.questions.generating')}</p>
            <p className="text-xs text-muted-foreground">{t('wizard.questions.generatingHint')}</p>
          </div>
        </div>
        {[1, 2, 3, 4].map((n) => (
          <Skeleton key={n} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-4">
      <div className="flex flex-1 min-h-0 flex-col gap-4 overflow-y-auto">
        {QUESTION_CATEGORIES.map((category: QuestionCategory) => (
          <QuestionCategorySection
            key={category}
            category={category}
            questions={getQuestionsForCategory(category)}
            onUpdate={(localIndex, updated) => {
              handleUpdate(category, localIndex, updated);
            }}
            onDelete={(localIndex) => {
              handleDelete(category, localIndex);
            }}
            onAdd={() => {
              handleAdd(category);
            }}
            onReorder={(activeId, overId) => {
              handleReorder(category, activeId, overId);
            }}
          />
        ))}
      </div>

      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onBack}>
          {t('wizard.back')}
        </Button>
        <Button onClick={onSave} disabled={isSaving || questions.length === 0}>
          {t('wizard.saveAndContinue')}
        </Button>
      </div>
    </div>
  );
}
